import { Divider, Group, Select } from '@byecode/ui'
import type { FieldProtocol } from '@lighthouse/core'
import { FlowSelectSourceType } from '@lighthouse/core'
import type { FindRecordActionConfig, FlowNode, NodeTypes } from '@lighthouse/shared'
import {
    getUpstreamRealDsId,
    ListItem4ByecodeUi,
    nodeTypeOptions,
    OUTPUT_MULTIPLE_RECORD_NODES,
    OUTPUT_SINGLE_DATA_NODES
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

import OutputVariable from '../../../OutputVariable'
import * as SC from '../../styles'
import { SelectRecordControlled } from '../component/SelectRecordControlled'

interface FindRecordActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    value: FindRecordActionConfig
    onChange: (value: FindRecordActionConfig) => void
}

export const FindRecordActionConfigure: React.FC<FindRecordActionConfigureProps> = ({ nodeType, allParentNodes, value, onChange }) => {
    const { nodeId, selectType, dataSourceId: dsId } = value
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    const dataSourceId = useMemo(() => {
        if (selectType === FlowSelectSourceType.UPSTREAM) {
            return getUpstreamRealDsId(nodeId, allParentNodes || [])
        }
        return dsId
    }, [allParentNodes, dsId, nodeId, selectType])

    const upstreamNodesForVariableSelect = useMemo(
        () => allParentNodes.filter(item => OUTPUT_SINGLE_DATA_NODES.has(item.data.nodeType)),
        [allParentNodes]
    )
    const upstreamNodesForSource = useMemo(
        () => allParentNodes.filter(item => OUTPUT_MULTIPLE_RECORD_NODES.has(item.data.nodeType)),
        [allParentNodes]
    )

    const outputOptions = useMemo(() => {
        const current: Record<string, FieldProtocol> = dataSourceList.find(item => item.id === dataSourceId)?.schema || {}

        const values = Object.values(current)

        return values.map(item => ({
            id: item.id,
            label: item.name,
            desc: '记录的字段值',
            type: item.innerType
        }))
    }, [dataSourceId, dataSourceList])

    return (
        <>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <Select disabled value={nodeType} options={nodeTypeOptions} />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>

            <Divider />

            <Group label="选择记录">
                <SelectRecordControlled
                    value={value}
                    onChange={onChange}
                    disableOmitAggregate
                    upstreamNodesForSource={upstreamNodesForSource}
                    upstreamNodesForVariableSelect={upstreamNodesForVariableSelect}
                />
            </Group>

            <OutputVariable options={outputOptions} noMt />
        </>
    )
}
